import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { cn } from "@city-sage/aurum-avis-labs-components";
import { useComments } from "../../lib/useComments";
import { PostPostableTypeEnum } from "@city-sage/core-client";
import { CreatePostSheet } from "./CreatePostSheet";

interface CommentButtonProps {
  postableType: 'itinerary' | 'stop';
  postableId: string;
  onClick?: () => void;
  theme?: 'light' | 'dark';
  layout?: 'vertical' | 'horizontal';
  className?: string;
}

export const CommentButton = ({
  postableType,
  postableId,
  onClick,
  theme = 'light',
  layout = 'vertical',
  className
}: CommentButtonProps) => {
  const { commentCount, isLoading } = useComments({ postableType, postableId });
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const isDark = theme === 'dark';
  const isHorizontal = layout === 'horizontal';

  const postableTypeEnum = postableType === 'itinerary' 
    ? PostPostableTypeEnum.Itinerary 
    : PostPostableTypeEnum.Stop;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else {
      setIsCreatePostOpen(true);
    }
  };

  const placeholderText = postableType === 'itinerary' 
    ? "Share your thoughts about this itinerary..." 
    : "Comment on this stop...";

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          "flex items-center justify-center gap-1.5 p-1 bg-transparent border-none cursor-pointer transition-transform active:scale-95 hover:scale-105 group",
          isHorizontal ? "flex-row" : "flex-col",
          className
        )}
        aria-label={`Comment (${isLoading ? 'loading' : commentCount} comments)`}
      >
        <MessageCircle 
          className={cn(
            "transition-colors duration-200",
            isHorizontal ? "w-5 h-5" : "w-7 h-7",
            isDark 
              ? "text-white drop-shadow-md group-hover:text-gray-100" 
              : "text-gray-900 group-hover:text-gray-700"
          )}
          strokeWidth={2}
        />
        <span className={cn(
          "font-semibold",
          isHorizontal ? "text-sm" : "text-xs",
          isDark ? "text-white drop-shadow-md" : "text-gray-900"
        )}>
          {isLoading ? "..." : commentCount}
        </span>
      </button>
      
      <CreatePostSheet
        isOpen={isCreatePostOpen}
        onClose={() => setIsCreatePostOpen(false)}
        postableType={postableTypeEnum}
        postableId={postableId}
        placeholder={placeholderText}
      />
    </>
  );
};
